import { useMemo, type ReactNode } from 'react';
import { useObserverAnchor } from '@/components/observer/useObserverAnchor';
import { cn } from '@/components/ui';

/*
  Общие полки экранов «Основы»: поле страницы и карточка-раздел. Вид — как на витрине:
  белая страница, карточки с тонкой рамкой, заголовок раздела надстрочником.

  Карточка — блок для помощника (spec-3): data-observer-block — на её кромку садится тело, когда цель —
  строка внутри; observe — адрес самой карточки как цели события (обслуживание cn338).
*/

export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-col pb-28', className)}>{children}</div>;
}

export function Body({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-col gap-4 px-8', className)}>{children}</div>;
}

export function Panel({
  title,
  aside,
  children,
  className,
  observe,
}: {
  title?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Адрес карточки в мире помощника: она — цель события. */
  observe?: string;
}) {
  const label = typeof title === 'string' ? title : '';
  const meta = useMemo(() => ({ role: 'block' as const, label }), [label]);
  const anchor = useObserverAnchor<HTMLElement>(observe ?? '', meta);
  return (
    <section
      ref={observe ? anchor : undefined}
      data-observer-block=""
      className={cn('rounded-lg border border-line bg-card p-5', className)}
    >
      {(title || aside) && (
        <div className="mb-4 flex items-center justify-between gap-3">
          {title && <h2 className="type-subheading text-fg">{title}</h2>}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}
